export type HomeReportLog = {
  id: string;
  text: string;
  isActive?: boolean;
};

type HomeReportSeenState = {
  version: 1;
  users: Record<string, string[]>;
};

export const HOME_REPORT_SEEN_STORAGE_KEY = "wizmate.home-report-seen.v1";

const MAX_SEEN_REPORTS_PER_USER = 100;

function hasStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeUserId(userId: string | null | undefined): string | null {
  if (typeof userId !== "string") return null;
  const normalized = userId.trim();
  return normalized || null;
}

function readState(): HomeReportSeenState {
  if (!hasStorage()) return { version: 1, users: {} };

  try {
    const parsed = JSON.parse(window.localStorage.getItem(HOME_REPORT_SEEN_STORAGE_KEY) ?? "null") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { version: 1, users: {} };
    }

    const candidate = parsed as { version?: unknown; users?: unknown };
    if (candidate.version !== 1 || !candidate.users || typeof candidate.users !== "object" || Array.isArray(candidate.users)) {
      return { version: 1, users: {} };
    }

    const users = Object.fromEntries(
      Object.entries(candidate.users as Record<string, unknown>).flatMap(([rawUserId, rawFingerprints]) => {
        const userId = normalizeUserId(rawUserId);
        if (!userId || !Array.isArray(rawFingerprints)) return [];
        const fingerprints = rawFingerprints
          .filter((value): value is string => typeof value === "string" && value.trim() !== "")
          .slice(-MAX_SEEN_REPORTS_PER_USER);
        return [[userId, fingerprints]];
      })
    );

    return { version: 1, users };
  } catch {
    return { version: 1, users: {} };
  }
}

export function createHomeReportFingerprint(log: HomeReportLog): string {
  const state = log.isActive === true ? "active" : "resolved";
  return JSON.stringify([String(log.id), state, String(log.text).trim()]);
}

export function getUnseenHomeReportFingerprints(
  userId: string | null | undefined,
  logs: HomeReportLog[]
): string[] {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId) return [];

  const seen = new Set(readState().users[normalizedUserId] ?? []);
  return Array.from(
    new Set(
      logs
        .filter((log) => log.isActive !== true)
        .map(createHomeReportFingerprint)
        .filter((fingerprint) => !seen.has(fingerprint))
    )
  );
}

export function markHomeReportsSeen(
  userId: string | null | undefined,
  logs: HomeReportLog[]
): void {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId || !hasStorage()) return;

  const state = readState();
  const next = [
    ...(state.users[normalizedUserId] ?? []),
    ...logs.filter((log) => log.isActive !== true).map(createHomeReportFingerprint),
  ];
  state.users[normalizedUserId] = Array.from(new Set(next)).slice(-MAX_SEEN_REPORTS_PER_USER);
  try {
    window.localStorage.setItem(HOME_REPORT_SEEN_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storageが利用できない環境でも、報告drawer自体は継続して利用できる。
  }
}
